import React from 'react'

import { StandardLayout } from '@factorialco/f0-react'
import { OneModal } from '@factorialco/f0-react/dist/experimental'
import { TimesheetPageShiftFragment } from 'generated/resources/graphql'

import { minutesToText } from 'modules/core/lib/duration'
import i18n from 'modules/core/lib/i18n'

import CustomTwoColumnsList from 'modules/attendance/components/CustomTwoColumnsList'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import { TimesheetFeatures } from 'modules/attendance/lib/timesheetFeatures'
import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'
import { calculateTimesheetBalanceBreakdown } from 'modules/attendance/lib/utils/timesheetBalanceBreakdownCalculations'

type Props = {
  onClose: () => void
  shifts: TimesheetPageShiftFragment[]
}

const TimesheetBalanceSidepanel = ({ onClose, shifts }: Props) => {
  const {
    aggregatedWorkedTime,
    estimatedTimes,
    currentEmployeesSetting,
    balanceMinutes,
    extraHoursMinutes,
    complementaryHoursMinutes,
    isPartTime,
    featureByName,
  } = useTimesheetData()
  const extraHoursBaseline = currentEmployeesSetting?.extraHoursBaseline
  const complementaryHoursBaseline = currentEmployeesSetting?.complementaryHoursBaseline
  const hasWorkingLocation = currentEmployeesSetting?.enableClockInLocation ?? false
  const hasComplementaryHoursFeature = TimesheetFeatures.COMPLEMENTARY_HOURS in featureByName
  const showComplementaryHours = hasComplementaryHoursFeature && isPartTime
  const {
    paidBreaksMinutes,
    paidAbsencesMinutes,
    effectiveWorkedMinutes,
    estimatedMinutes,
    extraTimeMinutes,
    trackedMinutes,
    unapprovedOvertimeMinutes,
    trackedMinutesDelta,
    workedMinutes,
    contractMinutes,
    balancedRegularMinutes,
  } = calculateTimesheetBalanceBreakdown({
    aggregatedWorkedTime,
    estimatedTimes,
    extraHoursMinutes,
    complementaryHoursMinutes: showComplementaryHours ? complementaryHoursMinutes : undefined,
  })

  const extraTimeMinutesText =
    extraTimeMinutes === undefined ? '-' : minutesToText(extraTimeMinutes, true)

  const hoursTypeLabel = showComplementaryHours
    ? i18n.t('time_tracking.timesheet.sidepanel.complementary_hours')
    : i18n.t('time_tracking.timesheet.sidepanel.extra_hours')

  const hoursTypeTooltipLabel = showComplementaryHours
    ? i18n.t('time_tracking.timesheet.sidepanel.tooltips.complementary_hours.label')
    : i18n.t('time_tracking.timesheet.sidepanel.tooltips.extra_hours.label')

  const hoursTypeTooltipDescription = showComplementaryHours
    ? i18n.t('time_tracking.timesheet.sidepanel.tooltips.complementary_hours.description')
    : i18n.t('time_tracking.timesheet.sidepanel.tooltips.extra_hours.description')

  const locationTypesWithPercentage = AttendanceShiftUtils.totalMinutesPerLocationPercentage(shifts)
  const workingLocationItems = Object.entries(locationTypesWithPercentage).map(([title, info]) => ({
    title,
    info,
  }))
  const showWorkingLocation = workingLocationItems.length > 0 && hasWorkingLocation

  return (
    <OneModal isOpen onClose={onClose} position="right">
      <OneModal.Header title={i18n.t('time_tracking.timesheet.sidepanel.title')} />
      <OneModal.Content>
        <StandardLayout>
          <CustomTwoColumnsList
            title={i18n.t('time_tracking.timesheet.sidepanel.total_balance')}
            titleValue={minutesToText(balanceMinutes, true, true)}
            titleTooltip={{
              label: i18n.t('time_tracking.timesheet.sidepanel.tooltips.balance.label'),
              description: i18n.t('time_tracking.timesheet.sidepanel.tooltips.balance.description'),
            }}
            list={[
              {
                title: i18n.t('time_tracking.timesheet.sidepanel.planned_time'),
                info: minutesToText(estimatedMinutes, true),
              },
              {
                title: i18n.t('time_tracking.timesheet.sidepanel.worked_time'),
                info: minutesToText(workedMinutes, true),
              },
            ]}
          />
          <CustomTwoColumnsList
            title={i18n.t('time_tracking.timesheet.sidepanel.worked_time')}
            titleValue={minutesToText(workedMinutes, true)}
            titleTooltip={{
              label: i18n.t('time_tracking.timesheet.sidepanel.tooltips.worked_time.label'),
              description: i18n.t(
                'time_tracking.timesheet.sidepanel.tooltips.worked_time.description'
              ),
            }}
            list={[
              {
                title: i18n.t('time_tracking.timesheet.sidepanel.regular_hours'),
                info: minutesToText(balancedRegularMinutes, true),
              },
              {
                title: i18n.t('time_tracking.timesheet.sidepanel.paid_breaks'),
                info: minutesToText(paidBreaksMinutes, true),
              },
              {
                title: hoursTypeLabel,
                info: extraTimeMinutesText,
              },
            ]}
          />
          <CustomTwoColumnsList
            title={i18n.t('time_tracking.timesheet.sidepanel.effective_worked_time')}
            titleValue={minutesToText(effectiveWorkedMinutes, true)}
            titleTooltip={{
              label: i18n.t(
                'time_tracking.timesheet.sidepanel.tooltips.effective_worked_time.label'
              ),
              description: i18n.t(
                'time_tracking.timesheet.sidepanel.tooltips.effective_worked_time.description'
              ),
            }}
            list={[
              {
                title: i18n.t('time_tracking.timesheet.sidepanel.paid_absences'),
                info: minutesToText(paidAbsencesMinutes, true),
              },
              {
                title: i18n.t('time_tracking.timesheet.sidepanel.worked_time'),
                info: minutesToText(workedMinutes, true),
              },
            ]}
          />
          <CustomTwoColumnsList
            title={hoursTypeLabel}
            titleValue={extraTimeMinutesText}
            titleTooltip={{
              label: hoursTypeTooltipLabel,
              description: hoursTypeTooltipDescription,
            }}
            list={[
              ...((showComplementaryHours ? complementaryHoursBaseline : extraHoursBaseline) ===
              'contract_hours'
                ? [
                    {
                      title: i18n.t('time_tracking.timesheet.sidepanel.contract_hours'),
                      info: minutesToText(contractMinutes, true),
                    },
                  ]
                : [
                    {
                      title: i18n.t('time_tracking.timesheet.sidepanel.planned_hours'),
                      info: minutesToText(estimatedMinutes, true),
                    },
                  ]),
              ...((showComplementaryHours ? complementaryHoursBaseline : extraHoursBaseline) ===
              'contract_hours'
                ? [
                    {
                      title: i18n.t('time_tracking.timesheet.sidepanel.effective_worked_time'),
                      info: minutesToText(effectiveWorkedMinutes, true),
                    },
                  ]
                : [
                    {
                      title: i18n.t('time_tracking.timesheet.sidepanel.worked_time'),
                      info: minutesToText(workedMinutes, true),
                    },
                  ]),
            ]}
          />
          <CustomTwoColumnsList
            title={i18n.t('time_tracking.timesheet.sidepanel.tracked_time')}
            titleValue={minutesToText(trackedMinutes, true)}
            titleTooltip={{
              label: i18n.t('time_tracking.timesheet.sidepanel.tooltips.tracked_time.label'),
              description: i18n.t(
                'time_tracking.timesheet.sidepanel.tooltips.tracked_time.description'
              ),
            }}
            list={[
              {
                title: i18n.t('time_tracking.timesheet.sidepanel.unapproved_overtime'),
                info:
                  unapprovedOvertimeMinutes > 0
                    ? `-${minutesToText(unapprovedOvertimeMinutes, true)}`
                    : minutesToText(0, true),
              },
              {
                title: i18n.t('time_tracking.timesheet.sidepanel.adjusted_time_tracked'),
                info:
                  trackedMinutesDelta !== 0
                    ? minutesToText(trackedMinutesDelta, true, true)
                    : minutesToText(0, true),
              },
            ]}
          />
          {showWorkingLocation && (
            <CustomTwoColumnsList
              title={i18n.t('time_tracking.timesheet.sidepanel.working_location')}
              list={workingLocationItems}
              titleTooltip={{
                label: i18n.t('time_tracking.timesheet.sidepanel.tooltips.working_location.label'),
                description: i18n.t(
                  'time_tracking.timesheet.sidepanel.tooltips.working_location.description'
                ),
              }}
            />
          )}
        </StandardLayout>
      </OneModal.Content>
    </OneModal>
  )
}

export default TimesheetBalanceSidepanel
